import { useContext, useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { SessionProvider } from "context/SessionContext";
import {
  ModalContext,
  ModalInstanceProvider,
  ModalProvider,
  ToastContext,
  ToastProvider,
} from "context";
import Navigation from "common/Navigation";
import OfflineAlert from "common/OfflineAlert";
import BetaAlert from "common/BetaAlert";
import Updater from "common/Updater";
import Home from "pages/Home";
import Explore from "pages/Explore";
import Adventure from "pages/Adventure";
import User, { Me } from "pages/User";
import SignIn from "pages/SignIn";
import SignUp from "pages/SignUp";
import ResetPassword from "pages/ResetPassword";
import { setNavigator } from "services/history";
import { useOnlineStatus } from "hooks";
import { flushQueue } from "./apollo/offlineLink";
import { tokenStore } from "./apollo/tokens";
import { refreshAccessToken, GRAPHQL_URI } from "services/auth";

/** Registers React Router's navigate into the history shim (v5-style usage). */
function HistoryBridge() {
  const navigate = useNavigate();
  useEffect(() => setNavigator((to, opts) => navigate(to, opts)), [navigate]);
  return null;
}

function ModalRoot() {
  const { close, unmount, stack } = useContext(ModalContext) as any;
  return Object.entries(stack).map(([id, state]: [string, any]) => {
    const { component: Component, props, isOpen } = state;
    const innerClose = () => close(id);
    const innerUnmount = () => unmount(id);
    return (
      <ModalInstanceProvider key={id} close={innerClose} unmount={innerUnmount} isOpen={isOpen}>
        <Component {...props} id={id} close={innerClose} />
      </ModalInstanceProvider>
    );
  });
}

function ToastRoot() {
  const { close, unmount, stack } = useContext(ToastContext) as any;
  const toasts = Object.entries(stack).map(([id, state]: [string, any]) => {
    const { component: Component, props, isOpen } = state;
    return (
      <Component
        {...props}
        key={id}
        id={id}
        close={() => close(id)}
        unmount={() => unmount(id)}
        isOpen={isOpen}
      />
    );
  });
  return (
    <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 11 }}>
      {toasts}
    </div>
  );
}

function UserRoute() {
  const { username } = useParams();
  return <User username={username} />;
}

/** On reconnect: refresh the access token, replay queued offline writes, refetch. */
function ReconnectSync() {
  const online = useOnlineStatus();
  const client = useApolloClient();
  const wasOnline = useRef(online);
  useEffect(() => {
    if (online && !wasOnline.current) {
      void (async () => {
        await refreshAccessToken();
        await flushQueue(GRAPHQL_URI, tokenStore.access);
        await client.reFetchObservableQueries();
      })().catch(() => {});
    }
    wasOnline.current = online;
  }, [online, client]);
  return null;
}

function Container() {
  return (
    <>
      <HistoryBridge />
      <ReconnectSync />
      <Navigation />
      <Updater />
      <OfflineAlert />
      <BetaAlert />
      <div className="main-content">
        <div className="container">
          <ModalRoot />
          <ToastRoot />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/me" element={<Me />} />
            <Route path="/me/:tab" element={<Me />} />
            <Route path="/users/:username" element={<UserRoute />} />
            <Route path="/users/:username/:tab" element={<UserRoute />} />
            <Route path="/adventures/:id" element={<Adventure />} />
            <Route path="/adventures/:id/post/:postId" element={<Adventure />} />
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/sign-up" element={<SignUp />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
          </Routes>
        </div>
      </div>
    </>
  );
}

export function App() {
  return (
    <SessionProvider>
      <ModalProvider>
        <ToastProvider>
          <Container />
        </ToastProvider>
      </ModalProvider>
    </SessionProvider>
  );
}

export default App;
